import { useState } from "react";
import { api } from "../lib/api";
import type { AdTile } from "../../shared/types";

export function AdminPage() {
  const [secret, setSecret] = useState("");
  const [dashboard, setDashboard] = useState<{ metrics: Record<string, number>; ads: AdTile[] } | null>(null);
  const emptyAd: Partial<AdTile> = { title: "", sponsorName: "", body: "", ctaLabel: "Learn more", targetUrl: "", placement: "feed", position: 3, status: "draft", background: "#003f34", textColor: "#ffffff" };
  const [ad, setAd] = useState<Partial<AdTile>>(emptyAd);
  const [editingAds, setEditingAds] = useState<Record<string, Partial<AdTile>>>({});
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function refreshDashboard() {
    setDashboard(await api.adminDashboard());
  }
  async function login(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await api.adminLogin(secret);
      await refreshDashboard();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Admin is locked.");
    }
  }
  async function createAd(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await api.createAd(ad);
      await refreshDashboard();
      setAd(emptyAd);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save ad.");
    }
  }
  function patchAd(id: string, changes: Partial<AdTile>) {
    setEditingAds((current) => ({ ...current, [id]: { ...current[id], ...changes } }));
  }
  async function updateExistingAd(item: AdTile, changes: Partial<AdTile> = {}) {
    setError(null);
    const next = { ...item, ...editingAds[item.id], ...changes };
    try {
      await api.updateAd(item.id, next);
      await refreshDashboard();
      setEditingAds((current) => {
        const copy = { ...current };
        delete copy[item.id];
        return copy;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update ad.");
    }
  }
  async function archiveExistingAd(item: AdTile) {
    setError(null);
    try {
      await api.archiveAd(item.id);
      await refreshDashboard();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive ad.");
    }
  }
  async function uploadAdMedia(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const media = await api.uploadAdMedia(file);
      setAd({ ...ad, mediaKey: media.key, mediaUrl: media.url });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not upload ad image.");
    } finally {
      setUploading(false);
    }
  }
  return (
    <section className="admin">
      <aside><h1>Floptropica One Admin</h1><a>Dashboard</a><a>Posts</a><a>Stories</a><a>Ads</a><a>Users</a><a>Reports</a><a>Settings</a></aside>
      <main>
        {!dashboard ? <form onSubmit={(event) => void login(event)} className="admin-login"><h2>Admin locked</h2><p>Requires `ADMIN_SECRET`; missing configuration fails closed.</p><input type="password" value={secret} onChange={(event) => setSecret(event.target.value)} placeholder="Admin secret" /><button>Unlock</button>{error && <p className="error">{error}</p>}</form> : (
          <div className="admin-grid">
            <section><h2>Dashboard</h2><div className="metric-grid">{Object.entries(dashboard.metrics).map(([key, value]) => <div className="metric" key={key}><span>{key}</span><strong>{value}</strong></div>)}</div></section>
            <section className="admin-panel"><h2>Advertising tiles</h2><form className="ad-form" onSubmit={(event) => void createAd(event)}>
              <input value={ad.title} onChange={(event) => setAd({ ...ad, title: event.target.value })} placeholder="Ad title" required />
              <input value={ad.sponsorName} onChange={(event) => setAd({ ...ad, sponsorName: event.target.value })} placeholder="Sponsor name" required />
              <textarea value={ad.body} onChange={(event) => setAd({ ...ad, body: event.target.value })} placeholder="Body" />
              <label className="admin-upload"><input type="file" accept="image/*" onChange={(event) => void uploadAdMedia(event.target.files?.[0])} />{uploading ? "Uploading..." : ad.mediaKey ? `Uploaded: ${ad.mediaKey}` : "Upload ad image"}</label>
              <input value={ad.targetUrl} onChange={(event) => setAd({ ...ad, targetUrl: event.target.value })} placeholder="Target URL" />
              <div className="admin-row"><select value={ad.placement} onChange={(event) => setAd({ ...ad, placement: event.target.value as AdTile["placement"] })}><option value="feed">Feed</option><option value="explore">Explore</option><option value="profile">Profile</option><option value="global">Global</option></select><input type="number" min={1} max={99} value={ad.position} onChange={(event) => setAd({ ...ad, position: Number(event.target.value) })} /></div>
              <div className="admin-row"><input type="color" value={ad.background} onChange={(event) => setAd({ ...ad, background: event.target.value })} /><input type="color" value={ad.textColor} onChange={(event) => setAd({ ...ad, textColor: event.target.value })} /><select value={ad.status} onChange={(event) => setAd({ ...ad, status: event.target.value as AdTile["status"] })}><option value="draft">Draft</option><option value="active">Active</option><option value="paused">Paused</option></select></div>
              <button>Save ad tile</button>
            </form>{error && <p className="error">{error}</p>}<div className="admin-ad-list">{dashboard.ads.map((item) => {
              const draft = { ...item, ...editingAds[item.id] };
              return <div className="admin-ad" key={item.id}>
                <span>Sponsored · {draft.placement} #{draft.position}</span>
                <strong>{item.title}</strong>
                <p>{item.sponsorName} · {draft.status}</p>
                <div className="admin-ad-controls">
                  <select value={draft.placement} onChange={(event) => patchAd(item.id, { placement: event.target.value as AdTile["placement"] })}>
                    <option value="feed">Feed</option>
                    <option value="explore">Explore</option>
                    <option value="profile">Profile</option>
                    <option value="global">Global</option>
                  </select>
                  <input type="number" min={1} max={99} value={draft.position} onChange={(event) => patchAd(item.id, { position: Number(event.target.value) })} />
                  <select value={draft.status} onChange={(event) => patchAd(item.id, { status: event.target.value as AdTile["status"] })}>
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="admin-actions">
                  <button type="button" onClick={() => void updateExistingAd(item)}>Save changes</button>
                  {item.status !== "active" && <button type="button" onClick={() => void updateExistingAd(item, { status: "active" })}>Activate</button>}
                  {item.status === "active" && <button type="button" onClick={() => void updateExistingAd(item, { status: "paused" })}>Pause</button>}
                  <button type="button" className="danger" onClick={() => void archiveExistingAd(item)}>Archive</button>
                </div>
              </div>;
            })}</div></section>
          </div>
        )}
      </main>
    </section>
  );
}
